import React, { useMemo } from 'react';
import { Table, Card, Descriptions, Statistic, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { QueryResponse, BatchGroup, SalaryItemRow } from '../../../services/pw9336Api';

interface ResultTableProps {
  result: QueryResponse;
}

const formatAmount = (v: number): string => (v ?? 0).toLocaleString();

const formatYearMonth = (ym: string): string => {
  if (ym && ym.length === 6) {
    return `${ym.substring(0, 4)} 年 ${ym.substring(4, 6)} 月`;
  }
  return ym || '';
};

/** Flatten batch data for table rendering: one row per salary item per batch. */
interface FlatRow {
  _key: string;
  batchNo: number;
  empCount: number;
  employees: string; // "E001 王小明, E002 陳大華"
  itemSeq: number;
  itemName: string;
  sign: string;
  amounts: number[];
  subtotal: number;
  total: number;
}

function flattenBatches(batches: BatchGroup[]): FlatRow[] {
  const rows: FlatRow[] = [];
  for (const batch of batches) {
    const empStr = batch.employees
      .map((e) => `${(e.empCode || '').trim()} ${(e.empName || '').trim()}`)
      .join(', ');
    for (const item of batch.items) {
      rows.push({
        _key: `${batch.batchNo}-${item.itemSeq}`,
        batchNo: batch.batchNo,
        empCount: batch.employees.length,
        employees: empStr,
        itemSeq: item.itemSeq,
        itemName: item.itemName,
        sign: item.sign,
        amounts: item.amounts,
        subtotal: item.subtotal,
        total: item.total,
      });
    }
  }
  return rows;
}

const ResultTable: React.FC<ResultTableProps> = ({ result }) => {
  const flatData = useMemo(() => flattenBatches(result.batches || []), [result.batches]);

  const columns: ColumnsType<FlatRow> = useMemo(() => {
    const cols: ColumnsType<FlatRow> = [
      {
        title: '批次',
        dataIndex: 'batchNo',
        key: 'batchNo',
        width: 60,
        align: 'center',
        onCell: (record: FlatRow) => {
          // Merge cells for same batch
          if (record.itemSeq === 1) {
            return { rowSpan: 6 };
          }
          return { rowSpan: 0 };
        },
      },
      {
        title: '薪資項目',
        key: 'itemName',
        width: 140,
        render: (_: unknown, record: FlatRow) => (
          <span>
            <Tag color={record.sign === '+' ? 'green' : 'red'}>{record.sign}</Tag>
            {record.itemName}
          </span>
        ),
      },
    ];

    // Add per-employee amount columns (up to 10 max across batches)
    const maxEmps = Math.max(...(result.batches || []).map((b) => b.employees.length), 0);
    for (let i = 0; i < maxEmps; i++) {
      const empIdx = i;
      cols.push({
        title: `#${i + 1}`,
        key: `emp_${i}`,
        width: 80,
        align: 'right',
        render: (_: unknown, record: FlatRow) => {
          if (empIdx < record.amounts.length) {
            return formatAmount(record.amounts[empIdx]);
          }
          return '';
        },
      });
    }

    cols.push(
      {
        title: '小計',
        dataIndex: 'subtotal',
        key: 'subtotal',
        width: 100,
        align: 'right',
        render: (v: number) => <strong>{formatAmount(v)}</strong>,
      },
      {
        title: '累計',
        dataIndex: 'total',
        key: 'total',
        width: 100,
        align: 'right',
        render: (v: number) => <strong>{formatAmount(v)}</strong>,
      },
    );

    return cols;
  }, [result.batches]);

  /** Employee header per batch */
  const batchHeaders = useMemo(() => {
    return (result.batches || []).map((batch) => ({
      batchNo: batch.batchNo,
      employees: batch.employees,
    }));
  }, [result.batches]);

  return (
    <div>
      <Card size="small" className="mb-4">
        <Descriptions title={`薪資印領清冊 — ${formatYearMonth(result.yearMonth)}`} size="small" column={4}>
          <Descriptions.Item label="員工人數">{result.totalEmps}</Descriptions.Item>
          <Descriptions.Item label="批次數">{(result.batches || []).length}</Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Employee header cards */}
      {batchHeaders.map((bh) => (
        <Card key={bh.batchNo} size="small" className="mb-2" title={`批次 ${bh.batchNo}`}>
          <div className="flex flex-wrap gap-3">
            {bh.employees.map((emp, idx) => (
              <Tag key={idx} color="blue">
                #{idx + 1} {(emp.empCode || '').trim()} {(emp.empName || '').trim()}
              </Tag>
            ))}
          </div>
        </Card>
      ))}

      {/* Salary items table */}
      <Table
        columns={columns}
        dataSource={flatData}
        rowKey="_key"
        size="small"
        scroll={{ x: 800, y: 500 }}
        pagination={false}
        bordered
      />

      {/* Grand totals */}
      <Card size="small" className="mt-4" title="合計">
        <div className="flex flex-wrap gap-6">
          <Statistic title="本薪 (+)" value={result.grandTotals.baseSalary} precision={0} prefix="$" />
          <Statistic title="退休金自願提繳 (-)" value={result.grandTotals.retirementVolDed} precision={0} prefix="$" />
          <Statistic title="加班費 (+)" value={result.grandTotals.overtimePay} precision={0} prefix="$" />
          <Statistic title="代扣稅額 (-)" value={result.grandTotals.taxWithholding} precision={0} prefix="$" />
          <Statistic title="勞保費 (-)" value={result.grandTotals.laborInsurance} precision={0} prefix="$" />
          <Statistic title="健保費 (-)" value={result.grandTotals.healthInsurance} precision={0} prefix="$" />
        </div>
      </Card>
    </div>
  );
};

export default ResultTable;
